// src/App.js
import { useEffect, useMemo, useRef, useState } from "react";
import ScriptPanel from "./components/ScriptPanel";
import SceneShotPanel from "./components/SceneShotPanel";
import { parseScenes, extractSceneNumber, getSceneNumberLabel } from "./utils/parseScenes";
import { normalizeTagValue } from "./utils/shotTags";
import logo from "./res/logo.png";
import APP_VERSION, { CHANGELOG } from "./version";
import { useI18n } from "./utils/i18n";
import { loadShotFieldLayout, saveShotFieldLayout } from "./utils/shotFieldLayout";
import { downloadTextFile } from "./utils/downloadTextFile";
import {
  DEFAULT_SHOT_FRAME_FORMAT,
  getShotFrameFormat,
} from "./utils/frameFormats";

// ---------------------------
// Keys (localStorage)
// ---------------------------
const SHOT_PREFIX_MODE_KEY = "shotlist_shotPrefixMode"; // "P" | "SH" | "none"
const SHOT_EDITOR_TERMS_LANG_KEY = "shotlist_shot_editor_terms_lang"; // "auto" | "fr" | "en"
const COUNT_LINKED_KEY = "shotlist_countLinkedShots"; // "1" | "0"
const SHOT_FRAME_FORMAT_KEY = "shotlist_shot_frame_format";
const LINKED_LETTER_REORDER_KEY = "shotlist_linkedLetterReorder"; // "1" | "0"
const SCENE_MOVE_MODE_KEY = "shotlist_sceneMoveMode"; // "text" | "visual"
const SHOT_REORDER_MOVES_TEXT_KEY = "shotlist_shotReorderMovesText"; // "1" | "0"

// ---------------------------
// Highlight presets (cycle)
// ---------------------------
const HIGHLIGHT_PRESETS = [
  "#3749BD", // bleu
  "#A74346", // rouge
  "#816C31", // jaune
  "#6F8C40", // vert
  "#2C6F85", // cyan
  "#573C7C", // violet
];

function getNextHighlightColor(prevShots, sceneId) {
  const countInScene = prevShots.filter((s) => s.sceneId === sceneId).length;
  return HIGHLIGHT_PRESETS[countInScene % HIGHLIGHT_PRESETS.length];
}

// When linking shots, the joining group inherits these fields
// (except estimation and assigned script text which must remain per shot).
function pickLinkedTags(src) {
  const s = src || {};
  return {
    shotSize: s.shotSize ?? "",
    angle: s.angle ?? s.shotType ?? "",
    shotType: s.shotType ?? s.angle ?? "", // legacy compat
    lensMm: s.lensMm ?? "",
    focus: s.focus ?? "",
    framing: s.framing ?? "",
    movement: s.movement ?? "",
    lights: s.lights ?? "",
  };
}

function makeId() {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `id_${Date.now()}_${Math.random().toString(16).slice(2)}`
  );
}

function alphaSuffix(i) {
  // 0->A, 1->B ... 25->Z, 26->AA, 27->AB...
  let n = i;
  let s = "";
  while (n >= 0) {
    s = String.fromCharCode((n % 26) + 65) + s;
    n = Math.floor(n / 26) - 1;
  }
  return s;
}

// Clé d'ordre d'un plan : ordre manuel s'il existe, sinon position dans le texte.
function shotOrderKey(sh) {
  return Number.isFinite(sh?.order) ? sh.order : (sh?.scriptRange?.start ?? 0);
}

// Clé d'ordre d'AFFICHAGE (visuel) : listOrder si présent, sinon ordre de numérotation.
function shotListKey(sh) {
  return Number.isFinite(sh?.listOrder) ? sh.listOrder : shotOrderKey(sh);
}

// Mirroir (module) du réglage "renuméroter les lettres des plans liés selon l'ordre".
// Piloté depuis App via un effet. Par défaut activé.
let linkedLetterReorderEnabled = true;

function renumberSceneShots(allShots, sceneId) {
  const inScene = allShots
    .filter((sh) => sh.sceneId === sceneId)
    .slice()
    // base = ordre de numérotation (order), pas l'ordre visuel
    .sort((a, b) => shotOrderKey(a) - shotOrderKey(b));

  // groupKey = linkGroupId if present, otherwise shot.id
  const groups = new Map(); // key -> shots[]
  for (const sh of inScene) {
    const key = sh.linkGroupId || sh.id;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(sh);
  }

  // base number per group in first-appearance order
  const baseByKey = new Map();
  let base = 1;
  for (const key of groups.keys()) {
    baseByKey.set(key, base++);
  }

  // Ordre des LETTRES au sein d'un groupe : selon l'ordre visuel (listOrder) si le
  // réglage est actif, sinon selon l'ordre de numérotation (comportement figé).
  const suffixKey = linkedLetterReorderEnabled ? shotListKey : shotOrderKey;
  const suffixIndexByKey = new Map(); // groupKey -> Map(shotId -> idx)
  for (const [key, arr] of groups.entries()) {
    const sorted = arr.slice().sort((a, b) => suffixKey(a) - suffixKey(b));
    suffixIndexByKey.set(key, new Map(sorted.map((s, i) => [s.id, i])));
  }

  const updatedInScene = inScene.map((sh) => {
    const key = sh.linkGroupId || sh.id;
    const arr = groups.get(key) || [sh];
    const baseNum = baseByKey.get(key) ?? 0;

    // if group is solo, clear linkGroupId (clean)
    if (arr.length <= 1) {
      return { ...sh, shotNumber: `S${baseNum}`, linkGroupId: null };
    }

    const idxInGroup = suffixIndexByKey.get(key)?.get(sh.id) ?? 0;
    const suf = alphaSuffix(Math.max(0, idxInGroup));
    return { ...sh, shotNumber: `S${baseNum}-${suf}`, linkGroupId: key };
  });

  const others = allShots.filter((sh) => sh.sceneId !== sceneId);
  const merged = [...others, ...updatedInScene];

  // ordre du tableau = ordre visuel
  merged.sort((a, b) => shotListKey(a) - shotListKey(b));
  return merged;
}

function normSceneKey(s) {
  return (s ?? "")
    .toString()
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // remove accents
    .replace(/[^A-Z0-9]+/g, " ")
    .trim();
}

function findSceneByPos(scenes, pos) {
  return (scenes || []).find((s) => (s.start ?? 0) <= pos && pos < (s.end ?? 0)) || null;
}

function adoptSceneIds(oldScenes, newParsedScenes, oldTextLen, newTextLen) {
  const oldList = Array.isArray(oldScenes) ? oldScenes : [];
  const newList = Array.isArray(newParsedScenes) ? newParsedScenes : [];

  if (newList.length === 0) return [];

  // index new scenes by normalized title
  const byTitle = new Map(); // key -> [idx...]
  newList.forEach((sc, i) => {
    const k = normSceneKey(sc.title);
    if (!byTitle.has(k)) byTitle.set(k, []);
    byTitle.get(k).push(i);
  });

  const used = new Set();
  const oldToNewIdx = new Map();

  // pass 1: exact title matches
  for (const oldSc of oldList) {
    const k = normSceneKey(oldSc.title);
    const candidates = byTitle.get(k) || [];
    const pick = candidates.find((i) => !used.has(i));
    if (pick != null) {
      oldToNewIdx.set(oldSc.id, pick);
      used.add(pick);
    }
  }

  // pass 2: fallback by relative position (keeps order)
  const oLen = Math.max(1, oldTextLen || 1);
  const nLen = Math.max(1, newTextLen || 1);

  for (const oldSc of oldList) {
    if (oldToNewIdx.has(oldSc.id)) continue;

    const target = Math.round(((oldSc.start ?? 0) / oLen) * nLen);

    let best = -1;
    let bestDist = Infinity;
    for (let i = 0; i < newList.length; i++) {
      if (used.has(i)) continue;
      const dist = Math.abs((newList[i].start ?? 0) - target);
      if (dist < bestDist) {
        best = i;
        bestDist = dist;
      }
    }
    if (best >= 0) {
      oldToNewIdx.set(oldSc.id, best);
      used.add(best);
    }
  }

  // invert: newIndex -> oldId
  const newIdxToOldId = new Map();
  for (const [oldId, idx] of oldToNewIdx.entries()) newIdxToOldId.set(idx, oldId);

  const oldById = new Map(oldList.map((o) => [o.id, o]));

  // adopt ids where possible + on conserve les réglages par scène (ratio / couleur)
  return newList.map((sc, i) => {
    const adoptedId = newIdxToOldId.get(i);
    if (!adoptedId) return sc;
    const oldSc = oldById.get(adoptedId);
    return {
      ...sc,
      id: adoptedId,
      frameFormat: oldSc?.frameFormat,
      colorFormat: oldSc?.colorFormat,
      deleted: oldSc?.deleted,
      uiOrder: oldSc?.uiOrder,
    };
  });
}

// --- helpers newline mapping (CRLF/LF safe) ---
function makeNewlineMap(text) {
  const raw = String(text ?? "");
  const prefix = new Uint32Array(raw.length + 1);
  for (let i = 0; i < raw.length; i++) {
    prefix[i + 1] = prefix[i] + (raw.charCodeAt(i) === 13 ? 1 : 0); // '\r'
  }

  const normLen = raw.length - prefix[raw.length];
  const rawToNorm = (pos) => {
    const p = Math.max(0, Math.min(raw.length, pos | 0));
    return p - prefix[p];
  };

  // monotone => binary search
  const normToRaw = (npos) => {
    const target = Math.max(0, Math.min(normLen, npos | 0));
    let lo = 0;
    let hi = raw.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      const midNorm = mid - prefix[mid];
      if (midNorm < target) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  };

  const normText = raw.replace(/\r\n?/g, "\n");
  return { raw, normText, normLen, rawToNorm, normToRaw };
}

function nlNorm(s) {
  return String(s ?? "").replace(/\r\n?/g, "\n");
}

function escapeRegExp(str) {
  return String(str ?? "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function makeWhitespaceFlexRegex(text) {
  const base = nlNorm(text).trim();
  if (!base) return null;

  // remplace tout bloc d'espaces/retours ligne par \s*
  const pattern = escapeRegExp(base).replace(/\s+/g, "\\s*");
  try {
    return new RegExp(pattern, "g");
  } catch {
    return null;
  }
}

function bestRegexOccurrenceNear(text, regex, predictedStart, maxHits = 2000) {
  if (!regex) return -1;
  regex.lastIndex = 0;

  let best = -1;
  let bestScore = Infinity;

  let m;
  let guard = 0;
  while ((m = regex.exec(text)) && guard++ < maxHits) {
    const at = m.index;
    const score = Math.abs(at - (predictedStart ?? 0));
    if (score < bestScore) {
      best = at;
      bestScore = score;
    }
    if (!m[0] || m[0].length === 0) regex.lastIndex++; // sécurité
  }
  return best;
}

function stripWs(s) {
  return nlNorm(s).replace(/\s+/g, "");
}

// ✅ Retourne {start,end} OU null si on ne retrouve pas le texte
function locateShotInNewTextOrNull({ oldText, newText, shot }) {
  const oldMap = makeNewlineMap(oldText);
  const newMap = makeNewlineMap(newText);

  const oldRange = shot?.scriptRange || { start: 0, end: 0 };
  const oldStartRaw = Math.max(0, Math.min(oldMap.raw.length, oldRange.start ?? 0));
  const oldEndRaw = Math.max(oldStartRaw, Math.min(oldMap.raw.length, oldRange.end ?? oldStartRaw));

  const snippetRaw =
    shot?.anchor?.snippet ?? oldMap.raw.slice(oldStartRaw, oldEndRaw) ?? "";
  const beforeRaw =
    shot?.anchor?.before ?? oldMap.raw.slice(Math.max(0, oldStartRaw - 40), oldStartRaw);
  const afterRaw =
    shot?.anchor?.after ?? oldMap.raw.slice(oldEndRaw, Math.min(oldMap.raw.length, oldEndRaw + 40));

  const snippet = nlNorm(snippetRaw);
  const before = nlNorm(beforeRaw);
  const after = nlNorm(afterRaw);

  // si snippet trop court => on ne peut pas décider “supprimé” de manière fiable
  if (snippet.trim().length < 4) return null;

  const probeOld = nlNorm(
    oldMap.raw.slice(oldStartRaw, Math.min(oldMap.raw.length, oldStartRaw + snippetRaw.length))
  );
  const looksLikeOldCoords = snippet && probeOld === snippet;

  const predictedInNewNorm = looksLikeOldCoords
    ? Math.round((oldMap.rawToNorm(oldStartRaw) / Math.max(1, oldMap.normLen)) * Math.max(1, newMap.normLen))
    : newMap.rawToNorm(oldStartRaw);

  // On essaie d'abord EXACT (rapide + précis)
  const combo1 = (before || "") + (snippet || "");
  let idx = combo1.trim().length >= 12
    ? bestOccurrenceNear(newMap.normText, combo1, predictedInNewNorm)
    : -1;
  if (idx >= 0) {
    const startN = idx + before.length;
    const endN = startN + snippet.length;
    return { start: newMap.normToRaw(startN), end: newMap.normToRaw(endN) };
  }

  idx = snippet.trim().length >= 8
    ? bestOccurrenceNear(newMap.normText, snippet, predictedInNewNorm)
    : -1;
  if (idx >= 0) {
    const startN = idx;
    const endN = idx + snippet.length;
    return { start: newMap.normToRaw(startN), end: newMap.normToRaw(endN) };
  }

  const combo2 = (snippet || "") + (after || "");
  idx = combo2.trim().length >= 12
    ? bestOccurrenceNear(newMap.normText, combo2, predictedInNewNorm)
    : -1;
  if (idx >= 0) {
    const startN = idx;
    const endN = idx + snippet.length;
    return { start: newMap.normToRaw(startN), end: newMap.normToRaw(endN) };
  }

  // ✅ Puis FLEX (tolère suppression de retours à la ligne / espaces)
  const rx = makeWhitespaceFlexRegex(snippet);
  const idxFlex = bestRegexOccurrenceNear(newMap.normText, rx, predictedInNewNorm);
  if (idxFlex >= 0) {
    rx.lastIndex = 0;
    const m = rx.exec(newMap.normText.slice(idxFlex));
    const matchLen = m && m.index === 0 ? m[0].length : snippet.length;

    const startN = idxFlex;
    const endN = idxFlex + Math.max(1, matchLen);
    return { start: newMap.normToRaw(startN), end: newMap.normToRaw(endN) };
  }

  // ✅ Introuvable => texte supprimé => shot doit disparaître
  return null;
}


function bestOccurrenceNear(text, pattern, predictedStart, maxHits = 2000) {
  if (!pattern) return -1;

  let best = -1;
  let bestScore = Infinity;
  let at = text.indexOf(pattern);
  let guard = 0;

  while (at !== -1 && guard++ < maxHits) {
    const score = Math.abs(at - (predictedStart ?? 0));
    if (score < bestScore) {
      best = at;
      bestScore = score;
    }
    at = text.indexOf(pattern, at + 1);
  }
  return best;
}

function locateShotInNewText({ oldText, newText, shot }) {
  const oldMap = makeNewlineMap(oldText);
  const newMap = makeNewlineMap(newText);

  const oldRange = shot?.scriptRange || { start: 0, end: 0 };
  const oldStartRaw = Math.max(0, Math.min(oldMap.raw.length, oldRange.start ?? 0));
  const oldEndRaw = Math.max(oldStartRaw, Math.min(oldMap.raw.length, oldRange.end ?? oldStartRaw));

  // anchor (compat si absent)
  const snippetRaw = shot?.anchor?.snippet ?? oldMap.raw.slice(oldStartRaw, oldEndRaw) ?? "";
  const beforeRaw = shot?.anchor?.before ?? oldMap.raw.slice(Math.max(0, oldStartRaw - 40), oldStartRaw);
  const afterRaw = shot?.anchor?.after ?? oldMap.raw.slice(oldEndRaw, Math.min(oldMap.raw.length, oldEndRaw + 40));

  const snippet = nlNorm(snippetRaw);
  const before = nlNorm(beforeRaw);
  const after = nlNorm(afterRaw);

  // detect whether the current oldRange really matches snippet in oldText
  const probeOld = nlNorm(oldMap.raw.slice(oldStartRaw, Math.min(oldMap.raw.length, oldStartRaw + snippetRaw.length)));
  const looksLikeOldCoords = snippet && probeOld === snippet;

  const predictedInNewNorm = looksLikeOldCoords
    ? Math.round((oldMap.rawToNorm(oldStartRaw) / Math.max(1, oldMap.normLen)) * Math.max(1, newMap.normLen))
    : newMap.rawToNorm(oldStartRaw); // treat oldStartRaw as already "new"

  // 1) before+snippet
  const combo1 = (before || "") + (snippet || "");
  let idx = combo1.trim().length >= 12 ? bestOccurrenceNear(newMap.normText, combo1, predictedInNewNorm) : -1;
  if (idx >= 0) {
    const startN = idx + before.length;
    const endN = startN + snippet.length;
    return { start: newMap.normToRaw(startN), end: newMap.normToRaw(endN) };
  }

  // 2) snippet alone
  idx = snippet.trim().length >= 8 ? bestOccurrenceNear(newMap.normText, snippet, predictedInNewNorm) : -1;
  if (idx >= 0) {
    const startN = idx;
    const endN = idx + snippet.length;
    return { start: newMap.normToRaw(startN), end: newMap.normToRaw(endN) };
  }

  // 3) snippet+after
  const combo2 = (snippet || "") + (after || "");
  idx = combo2.trim().length >= 12 ? bestOccurrenceNear(newMap.normText, combo2, predictedInNewNorm) : -1;
  if (idx >= 0) {
    const startN = idx;
    const endN = idx + snippet.length;
    return { start: newMap.normToRaw(startN), end: newMap.normToRaw(endN) };
  }

  // fallback: proportional mapping (norm -> raw)
  const oldStartN = oldMap.rawToNorm(oldStartRaw);
  const oldEndN = oldMap.rawToNorm(oldEndRaw);
  const lenN = Math.max(1, (snippet?.length ?? 0), oldEndN - oldStartN);

  const startN = Math.max(0, Math.min(newMap.normLen - 1, predictedInNewNorm));
  const endN = Math.max(startN + 1, Math.min(newMap.normLen, startN + lenN));
  return { start: newMap.normToRaw(startN), end: newMap.normToRaw(endN) };
}

function shiftRangeForEdit(range, replaceStart, replaceEnd, delta) {
  if (!range) return range;

  let s = range.start ?? 0;
  let e = range.end ?? s;

  // edit entirely before => full shift
  if (s >= replaceEnd) return { start: s + delta, end: e + delta };

  // edit entirely after => unchanged
  if (e <= replaceStart) return { start: s, end: e };

  // overlap => keep start, adjust end best-effort
  const newEnd = Math.max(s, e + delta);
  return { start: s, end: newEnd };
}

function clampRangeToText(range, len) {
  const s0 = range?.start ?? 0;
  const e0 = range?.end ?? s0;
  const s = Math.max(0, Math.min(len, s0));
  const e = Math.max(s, Math.min(len, e0));
  return { start: s, end: e };
}

function buildAnchorFromText(text, range, ctx = 40) {
  const t = String(text ?? "");
  const r = clampRangeToText(range, t.length);
  const snippet = t.slice(r.start, r.end);
  const before = t.slice(Math.max(0, r.start - ctx), r.start);
  const after = t.slice(r.end, Math.min(t.length, r.end + ctx));
  return { snippet, before, after };
}

function computeEditSpan(prevText, nextText) {
  const a = String(prevText ?? "");
  const b = String(nextText ?? "");
  const min = Math.min(a.length, b.length);

  let start = 0;
  while (start < min && a[start] === b[start]) start++;

  let aEnd = a.length;
  let bEnd = b.length;
  while (aEnd > start && bEnd > start && a[aEnd - 1] === b[bEnd - 1]) {
    aEnd--;
    bEnd--;
  }

  return { replaceStart: start, replaceEnd: aEnd };
}

// Réordonne, DANS LE TEXTE, les blocs de texte assigné des plans d'une scène pour
// qu'ils suivent l'ordre `orderedIds`. Les « remplissages » (en-tête, lignes non
// assignées) restent à leur emplacement ; seuls les contenus des plans sont permutés.
// La longueur totale du texte est conservée (les autres scènes ne bougent pas).
// Retourne { text, newRangesById } ou null s'il n'y a rien à faire.
function reorderSceneTextForOrder(text, allShots, sceneId, orderedIds) {
  const byPos = allShots
    .filter(
      (s) =>
        s.sceneId === sceneId && (s.scriptRange?.end ?? 0) > (s.scriptRange?.start ?? 0)
    )
    .slice()
    .sort((a, b) => (a.scriptRange.start ?? 0) - (b.scriptRange.start ?? 0));

  const k = byPos.length;
  if (k < 2) return null;

  const withText = new Set(byPos.map((s) => s.id));
  const orderIds = orderedIds.filter((id) => withText.has(id));
  if (orderIds.length !== k) return null;

  // Ordre inchangé => rien à faire.
  const posOrder = byPos.map((s) => s.id);
  if (orderIds.every((id, i) => id === posOrder[i])) return null;

  const shotById = new Map(allShots.map((s) => [s.id, s]));
  const rStart = byPos[0].scriptRange.start;
  const rEnd = byPos[k - 1].scriptRange.end;

  let region = "";
  const newRangesById = new Map();
  let cursor = rStart;
  for (let i = 0; i < k; i++) {
    const slot = byPos[i].scriptRange;
    region += text.slice(cursor, slot.start); // remplissage avant ce bloc
    const cs = shotById.get(orderIds[i]);
    const content = text.slice(cs.scriptRange.start, cs.scriptRange.end);
    const absStart = rStart + region.length;
    region += content;
    newRangesById.set(orderIds[i], { start: absStart, end: rStart + region.length });
    cursor = slot.end;
  }

  const newText = text.slice(0, rStart) + region + text.slice(rEnd);
  return { text: newText, newRangesById };
}

function ChangelogModal({ onClose, changelog, t }) {
  // Fermer avec ESC + lock scroll
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const labels = useMemo(() => {
    const fallback = {
      title: "Journal des mises à jour",
      close: "Fermer",
      added: "Ajouts de fonctions",
      improved: "Améliorations",
      fixed: "Correction de bugs",
      empty: "Aucune entrée.",
    };
    if (!t) return fallback;
    return {
      title: t("changelog.title"),
      close: t("changelog.close"),
      added: t("changelog.added"),
      improved: t("changelog.improved"),
      fixed: t("changelog.fixed"),
      empty: t("changelog.empty"),
    };
  }, [t]);

  const renderSection = (title, items) => {
    if (!items || items.length === 0) return null;
    return (
      <div className="changelogSection">
        <div className="changelogSectionTitle">{title}</div>
        <ul className="changelogList">
          {items.map((x, i) => (
            <li key={`${title}-${i}`}>{x}</li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div
      className="modalOverlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div className="modalCard modalW_changelog changelogModal" role="dialog" aria-modal="true">
        <div className="modalHeader">
          <div className="modalTitle">{labels.title}</div>

          <button
            type="button"
            className="modalCloseButton"
            onClick={onClose}
            aria-label={labels.close}
            title={labels.close}
          >
            ✕
          </button>
        </div>

        <div className="modalBody">
          {!changelog || changelog.length === 0 ? (
            <div className="modalEmpty">{labels.empty}</div>
          ) : (
            changelog.map((entry) => (
              <div key={entry.version} className="changelogEntry">
                <div className="changelogEntryHeader">
                  <div className="changelogVersion">{entry.version}</div>
                  {entry.date ? <div className="changelogDate">{entry.date}</div> : null}
                </div>

                {renderSection(labels.added, entry.added)}
                {renderSection(labels.improved, entry.improved)}
                {renderSection(labels.fixed, entry.fixed)}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [scriptText, setScriptText] = useState("");
  const [isChangelogOpen, setIsChangelogOpen] = useState(false);

  // Projet : nom de fichier, "modifié depuis dernière sauvegarde", modales
  const [fileName, setFileName] = useState("");
  const [aboutOpen, setAboutOpen] = useState(false);
  const [savePopupOpen, setSavePopupOpen] = useState(false);
  const [projectDirty, setProjectDirty] = useState(false);
  const dirtyPrevRef = useRef(); // valeurs précédentes (détecte un vrai changement)
  const dirtyCleanRef = useRef(false); // le prochain changement vient d'un load => pas "dirty"
  const saveRef = useRef(() => {});

  const { t, lang } = useI18n();

  const [syncToast, setSyncToast] = useState(null);
  const toastTimerRef = useRef(null);

  const showSyncToast = (msg) => {
    if (!msg) return;
    try {
      clearTimeout(toastTimerRef.current);
    } catch {}
    setSyncToast(String(msg));
    toastTimerRef.current = setTimeout(() => setSyncToast(null), 3500);
  };

  const [shotFrameFormat, setShotFrameFormat] = useState(() => {
  const saved = localStorage.getItem(SHOT_FRAME_FORMAT_KEY);
  return getShotFrameFormat(saved).value || DEFAULT_SHOT_FRAME_FORMAT;
  });

  useEffect(() => {
    localStorage.setItem(SHOT_FRAME_FORMAT_KEY, shotFrameFormat);
  }, [shotFrameFormat]);

  // ---------------------------
  // Prefix mode: FR => P, EN => SH (unless "none")
  // ---------------------------
  const defaultPrefixForLang = (l) =>
    String(l || "").toLowerCase().startsWith("fr") ? "P" : "SH";

  const [shotPrefixMode, setShotPrefixMode] = useState(() => {
    const saved = localStorage.getItem(SHOT_PREFIX_MODE_KEY);
    if (saved === "P" || saved === "SH" || saved === "none") return saved;
    return defaultPrefixForLang(lang);
  });

  useEffect(() => {
    localStorage.setItem(SHOT_PREFIX_MODE_KEY, shotPrefixMode);
  }, [shotPrefixMode]);

  // ✅ On language change: auto-pick default prefix (unless "none")
  const prevLangRef = useRef(lang);
  useEffect(() => {
    if (prevLangRef.current === lang) return;
    prevLangRef.current = lang;

    setShotPrefixMode((prev) => {
      if (prev === "none") return prev;
      return defaultPrefixForLang(lang);
    });
  }, [lang]);

  // ---------------------------
  // Terms language for Shot Editor fields (auto/fr/en)
  // ---------------------------
  const [shotEditorTermsLang, setShotEditorTermsLang] = useState(() => {
    const saved = localStorage.getItem(SHOT_EDITOR_TERMS_LANG_KEY);
    if (saved === "auto" || saved === "fr" || saved === "en") return saved;
    return "auto";
  });

  useEffect(() => {
    localStorage.setItem(SHOT_EDITOR_TERMS_LANG_KEY, shotEditorTermsLang);
  }, [shotEditorTermsLang]);

  // ---------------------------
  // Global layout/style for shot fields (order + tag style)
  // ---------------------------
  const [shotFieldLayout, setShotFieldLayout] = useState(() => {
    try {
      const v = loadShotFieldLayout?.();
      return Array.isArray(v) ? v : null; // null => SceneShotPanel fallback layout
    } catch {
      return null;
    }
  });

  useEffect(() => {
    try {
      saveShotFieldLayout?.(shotFieldLayout);
    } catch {
      // noop
    }
  }, [shotFieldLayout]);

  // ---------------------------
  // Scenes / Shots state
  // ---------------------------
  const [scenes, setScenes] = useState([]);
  const [selectedSceneId, setSelectedSceneId] = useState(null);

  // Ordre d'affichage des scènes : ordre manuel (uiOrder) s'il a été défini via
  // le mode organisation "visuel", sinon ordre du texte (start).
  const orderedScenes = useMemo(() => {
    const hasUi = scenes.some((s) => Number.isFinite(s.uiOrder));
    return scenes.slice().sort((a, b) => {
      const ka = hasUi ? (a.uiOrder ?? a.start ?? 0) : (a.start ?? 0);
      const kb = hasUi ? (b.uiOrder ?? b.start ?? 0) : (b.start ?? 0);
      return ka - kb;
    });
  }, [scenes]);

  const [shots, setShots] = useState([]);
  const [selectedShotId, setSelectedShotId] = useState(null);

  const [organizeMode, setOrganizeMode] = useState(false); // mode organisation (scènes/plans)
  const [scriptViewMode, setScriptViewMode] = useState("full"); // "full" | "scene"
  const [scriptCollapsed, setScriptCollapsed] = useState(false); // ✅ replier le panneau scénario
  const [editorInScriptPanel, setEditorInScriptPanel] = useState(false); // ✅ Shot Editor à la place du scénario
  const [sceneSelection, setSceneSelection] = useState({ start: 0, end: 0 }); // relatif scène
  const [scriptSelection, setScriptSelection] = useState({ start: 0, end: 0 }); // global

  const liveEditsSinceSyncRef = useRef(false);

  const [includeLinkedShotsInCounts, setIncludeLinkedShotsInCounts] = useState(() => {
    const raw = localStorage.getItem(COUNT_LINKED_KEY);
    if (raw == null) return true; // ✅ default = true
    return raw === "1" || raw === "true";
  });

  useEffect(() => {
    localStorage.setItem(COUNT_LINKED_KEY, includeLinkedShotsInCounts ? "1" : "0");
  }, [includeLinkedShotsInCounts]);

  // Réglage : renuméroter les lettres des plans liés selon l'ordre d'affichage.
  const [linkedLetterReorder, setLinkedLetterReorder] = useState(() => {
    const raw = localStorage.getItem(LINKED_LETTER_REORDER_KEY);
    const v = raw == null ? true : raw === "1" || raw === "true"; // ✅ défaut = activé
    linkedLetterReorderEnabled = v; // synchronise le mirroir module dès le départ
    return v;
  });

  useEffect(() => {
    localStorage.setItem(LINKED_LETTER_REORDER_KEY, linkedLetterReorder ? "1" : "0");
    linkedLetterReorderEnabled = linkedLetterReorder; // mirroir module pour renumberSceneShots
  }, [linkedLetterReorder]);

  // Réglage : mode de déplacement des scènes en mode organisation.
  // "text" (défaut) = déplace physiquement le bloc de texte dans le scénario.
  // "visual" = réordonne seulement l'affichage (ordre manuel), sans toucher au texte.
  const [sceneMoveMode, setSceneMoveMode] = useState(() => {
    const raw = localStorage.getItem(SCENE_MOVE_MODE_KEY);
    return raw === "visual" ? "visual" : "text";
  });

  useEffect(() => {
    localStorage.setItem(SCENE_MOVE_MODE_KEY, sceneMoveMode);
  }, [sceneMoveMode]);

  // Réglage : réordonner aussi le texte du scénario quand on renumérote / réordonne
  // des plans dans une scène. Activé par défaut, désactivable.
  const [shotReorderMovesText, setShotReorderMovesText] = useState(() => {
    const raw = localStorage.getItem(SHOT_REORDER_MOVES_TEXT_KEY);
    return raw == null ? true : raw === "1" || raw === "true";
  });

  useEffect(() => {
    localStorage.setItem(SHOT_REORDER_MOVES_TEXT_KEY, shotReorderMovesText ? "1" : "0");
  }, [shotReorderMovesText]);

  // ---------------------------
  // Live edit delta: shift ranges (scenes + shots)
  // ---------------------------
  const handleScriptEditDelta = ({ prevText, nextText, replaceStart, replaceEnd }) => {
    const prev = nlNorm(prevText);
    const next = nlNorm(nextText);

    let rs = Number.isFinite(replaceStart) ? replaceStart : null;
    let re = Number.isFinite(replaceEnd) ? replaceEnd : null;

    const hintOk =
      rs !== null &&
      re !== null &&
      rs >= 0 &&
      re >= rs &&
      re <= prev.length &&
      prev.slice(0, rs) === next.slice(0, rs);

    if (!hintOk) {
      ({ replaceStart: rs, replaceEnd: re } = computeEditSpan(prev, next));
    }

    const delta = next.length - prev.length;

    liveEditsSinceSyncRef.current = true;
    if (!delta && replaceStart === replaceEnd) return;

    // 1) shift scenes to avoid overflow
    setScenes((prevScenes) => {
      const shifted = prevScenes.map((sc) => {
        const r = shiftRangeForEdit({ start: sc.start, end: sc.end }, replaceStart, replaceEnd, delta);
        return { ...sc, start: r.start, end: r.end };
      });

      const sorted = shifted.slice().sort((a, b) => (a.start ?? 0) - (b.start ?? 0));
      const textLen = nextText?.length ?? scriptText.length;

      for (let i = 0; i < sorted.length; i++) {
        const nextStart = sorted[i + 1]?.start ?? textLen;
        const s = sorted[i].start ?? 0;
        sorted[i] = { ...sorted[i], end: Math.max(s, nextStart) };
      }

      return sorted;
    });

    // 2) shift shots ranges
    setShots((prevShots) =>
      prevShots.map((sh) => {
        if (!sh?.scriptRange) return sh;
        const nextRange = shiftRangeForEdit(sh.scriptRange, replaceStart, replaceEnd, delta);
        return { ...sh, scriptRange: nextRange };
      })
    );
  };

  // ---------------------------
  // Apply script edits (sync / remap)
  // ---------------------------
  const applyScriptEdits = ({ baseText, nextText, mode = "editor" }) => {
    const oldText = nlNorm(baseText);
    const newText = nlNorm(nextText);

    if (oldText === newText) {
      liveEditsSinceSyncRef.current = false;
      return;
    }

    // If import/file reload => force anchor remap
    const keepRanges = mode !== "file" && liveEditsSinceSyncRef.current;
    const trustRanges = !!liveEditsSinceSyncRef.current;

    const parsed = parseScenes(newText);
    const newScenes = adoptSceneIds(scenes, parsed, oldText.length, newText.length);

    const remapOne = (sh) => {
      const cur = clampRangeToText(sh.scriptRange, newText.length);

      // create anchor if missing (base depends if we trust current coords)
      const anchorBase = trustRanges ? newText : oldText;
      const anchor = sh.anchor?.snippet ? sh.anchor : buildAnchorFromText(anchorBase, sh.scriptRange, 40);

      let range = cur;

      if (!keepRanges) {
        range = locateShotInNewTextOrNull({ oldText, newText, shot: { ...sh, anchor } });
      } else {
        // mode éditeur : on garde le range SI le texte correspond toujours (même sans retours ligne)
        const curSlice = newText.slice(cur.start, cur.end);
        const ok = stripWs(curSlice) === stripWs(anchor.snippet || "");

        if (ok) {
          range = cur;
        } else {
          // sinon on tente de le retrouver ailleurs ; si introuvable => supprimé
          range = locateShotInNewTextOrNull({ oldText, newText, shot: { ...sh, anchor } });
        }
      }

      if (!range) return null; // ✅ supprimé

      const scene = findSceneByPos(newScenes, range.start) || newScenes[0] || null;
      const nextAnchor = buildAnchorFromText(newText, range, 40);

      return {
        ...sh,
        sceneId: scene?.id ?? sh.sceneId,
        scriptRange: range,
        anchor: nextAnchor,
      };
    };

    let deletedCount = 0;

    let nextShots = (shots || []).reduce((acc, sh) => {
      const mapped = remapOne(sh);
      if (!mapped) {
        deletedCount += 1;
        return acc;
      }
      acc.push(mapped);
      return acc;
    }, []);

    for (const sc of newScenes) nextShots = renumberSceneShots(nextShots, sc.id);

    // ✅ Notification si suppression
    if (deletedCount > 0) {
      showSyncToast(t("script.refresh.deletedShots", { count: deletedCount }));
    }

    setScriptText(newText);
    setScenes(newScenes);
    setShots(nextShots);

    const still = newScenes.some((s) => s.id === selectedSceneId);
    setSelectedSceneId(still ? selectedSceneId : (newScenes[0]?.id ?? null));

    liveEditsSinceSyncRef.current = false;
  };

  // ---------------------------
  // Script load / view toggle
  // ---------------------------
  const handleScriptLoaded = (text) => {
    dirtyCleanRef.current = true;
    setProjectDirty(false);
    setScriptText(text);

    const parsed = parseScenes(text);
    setScenes(parsed);
    setSelectedSceneId(parsed[0]?.id ?? null);

    setShots([]);
    setSelectedShotId(null);

    setScriptViewMode("full");
    setSceneSelection({ start: 0, end: 0 });
    setScriptSelection({ start: 0, end: 0 });
  };

  // ---------------------------
  // Sauvegarde du projet (déplacée dans la barre du haut)
  // ---------------------------
  const canSaveProject = Boolean(scriptText?.trim()) || Boolean((shots ?? []).length);

  const saveProjectFile = () => {
    if (!canSaveProject) return;

    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");

    const base = (fileName || "project").replace(/\.[^/.]+$/, "").trim() || "project";
    const filename = `${base}_project_${y}-${m}-${d}.shotlist.json`;

    const project = {
      schema: "shotlist-builder-project",
      schemaVersion: 1,
      appVersion: APP_VERSION,
      savedAt: now.toISOString(),
      fileName,
      scriptText,
      scenes,
      shots,
      selectedSceneId,
      selectedShotId,
      scriptViewMode,
    };

    downloadTextFile(filename, JSON.stringify(project, null, 2), "application/json;charset=utf-8");
    setProjectDirty(false);
  };

  const handleSaveProject = () => {
    if (!canSaveProject) return;
    saveProjectFile();
    setSavePopupOpen(true);
  };

  // garder une référence à jour pour le raccourci clavier
  saveRef.current = handleSaveProject;

  // Ctrl/Cmd + S => sauvegarder
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "S")) {
        e.preventDefault();
        saveRef.current?.();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Disclaimer avant fermeture de l'onglet s'il y a du contenu
  useEffect(() => {
    const onBeforeUnload = (e) => {
      const hasContent = Boolean(scriptText && scriptText.trim()) || Boolean((shots ?? []).length);
      if (!hasContent) return;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [scriptText, shots]);

  // Marque le projet comme "modifié" dès qu'un vrai changement survient.
  useEffect(() => {
    if (dirtyPrevRef.current === undefined) {
      dirtyPrevRef.current = { scriptText, scenes, shots };
      return;
    }
    const p = dirtyPrevRef.current;
    const changed =
      p.scriptText !== scriptText || p.scenes !== scenes || p.shots !== shots;
    dirtyPrevRef.current = { scriptText, scenes, shots };
    if (!changed) return;
    if (dirtyCleanRef.current) {
      dirtyCleanRef.current = false; // changement dû à un import/chargement
      return;
    }
    setProjectDirty(true);
  }, [scriptText, scenes, shots]);

  const toggleScriptView = () => {
    setSceneSelection({ start: 0, end: 0 });
    setScriptSelection({ start: 0, end: 0 });
    setScriptViewMode((v) => (v === "full" ? "scene" : "full"));
  };

  // ---------------------------
  // Shots CRUD
  // ---------------------------
  const addShotFromSelection = ({ sceneId, selStart, selEnd }) => {
    const scene = scenes.find((s) => s.id === sceneId);
    if (!scene) return;

    const a = Math.min(selStart, selEnd);
    const b = Math.max(selStart, selEnd);
    if (b - a <= 0) return;

    const globalStart = scene.start + a;
    const globalEnd = scene.start + b;

    const newId = makeId();

    setShots((prev) => {
      const nextColor = getNextHighlightColor(prev, sceneId);

      const snippet = scriptText.slice(globalStart, globalEnd);
      const before = scriptText.slice(Math.max(0, globalStart - 40), globalStart);
      const after = scriptText.slice(globalEnd, Math.min(scriptText.length, globalEnd + 40));

      const newShot = {
        id: newId,
        sceneId,
        scriptRange: { start: globalStart, end: globalEnd },
        shotNumber: "",
        description: "",
        shotSize: "",
        angle: "",
        shotType: "", // legacy compat
        lensMm: "",
        focus: "",
        framing: "",
        movement: "",
        lights: "",
        durationEstimateSec: 0,
        linkGroupId: null,
        image: null,
        isMaster: false,
        highlightColor: nextColor,
        anchor: { snippet, before, after },
      };

      const next = [...prev, newShot];
      return renumberSceneShots(next, sceneId);
    });

    setSelectedShotId(newId);
  };

  // ✅ Ajout d'un plan "insert" sans sélection de texte du scénario.
  // Le plan est rattaché à la scène mais sans plage de texte assignée.
  const addShotWithoutText = ({ sceneId }) => {
    const scene = scenes.find((s) => s.id === sceneId);
    if (!scene) return;

    const newId = makeId();
    const anchorPos = scene.start ?? 0;

    setShots((prev) => {
      const nextColor = getNextHighlightColor(prev, sceneId);

      const newShot = {
        id: newId,
        sceneId,
        scriptRange: { start: anchorPos, end: anchorPos }, // ✅ plage vide (insert)
        shotNumber: "",
        description: "",
        shotSize: "",
        angle: "",
        shotType: "",
        lensMm: "",
        focus: "",
        framing: "",
        movement: "",
        lights: "",
        durationEstimateSec: 0,
        linkGroupId: null,
        image: null,
        isMaster: false,
        highlightColor: nextColor,
        anchor: { snippet: "", before: "", after: "" },
      };

      const next = [...prev, newShot];
      return renumberSceneShots(next, sceneId);
    });

    setSelectedShotId(newId);
  };

  // Import d'un découpage CSV : crée des plans (inserts) rattachés aux scènes.
  // records: [{ sceneRef, description, shotSize, angle, focus, framing,
  //             movement, lensMm, lights, durationEstimateSec, isMaster }]
  const importShotsFromCsv = (records) => {
    if (!Array.isArray(records) || records.length === 0) return 0;
    if (!scenes.length) {
      showSyncToast(t("csv.needScenes"));
      return 0;
    }

    const findScene = (ref) => {
      const key = normSceneKey(ref);
      if (!key) return scenes[0] || null;

      // par numéro de scène (label) ou index+1
      const byNum = scenes.find(
        (s, i) =>
          normSceneKey(getSceneNumberLabel(s, i)) === key ||
          normSceneKey(String(i + 1)) === key
      );
      if (byNum) return byNum;

      // par titre (inclusion dans un sens ou l'autre)
      const byTitle = scenes.find((s) => {
        const tk = normSceneKey(s.title);
        return tk && (tk === key || tk.includes(key) || key.includes(tk));
      });
      return byTitle || scenes[0] || null;
    };

    const norm = (key, v) => {
      const raw = String(v ?? "").trim();
      if (!raw) return "";
      const canon = normalizeTagValue(key, raw);
      return canon || raw; // garde la valeur brute si non reconnue
    };

    let created = 0;

    setShots((prev) => {
      let next = prev.slice();
      const affected = new Set();

      for (const rec of records) {
        // ✅ priorité au rattachement explicite (table de correspondance), sinon heuristique
        const scene =
          (rec.sceneId && scenes.find((s) => s.id === rec.sceneId)) ||
          findScene(rec.sceneRef);
        if (!scene) continue;

        const pos = scene.start ?? 0;
        const angleVal = norm("angle", rec.angle);

        next.push({
          id: makeId(),
          sceneId: scene.id,
          scriptRange: { start: pos, end: pos }, // insert (sans texte assigné)
          shotNumber: "",
          description: String(rec.description ?? "").trim(),
          shotSize: norm("shotSize", rec.shotSize),
          angle: angleVal,
          shotType: angleVal, // legacy compat
          lensMm: String(rec.lensMm ?? "").trim(),
          focus: norm("focus", rec.focus),
          framing: norm("framing", rec.framing),
          movement: norm("movement", rec.movement),
          lights: String(rec.lights ?? "").trim(),
          durationEstimateSec: (() => {
            const n = Number(String(rec.durationEstimateSec ?? "").replace(/[^\d.]/g, ""));
            return Number.isFinite(n) ? n : 0;
          })(),
          linkGroupId: null,
          image: null,
          isMaster: !!rec.isMaster,
          highlightColor: getNextHighlightColor(next, scene.id),
          anchor: { snippet: "", before: "", after: "" },
        });

        affected.add(scene.id);
        created += 1;
      }

      for (const sid of affected) next = renumberSceneShots(next, sid);
      return next;
    });

    showSyncToast(t("csv.imported", { count: created }));
    return created;
  };

  // Réglages par scène (ratio d'écran / format couleur appliqués à tous ses plans)
  const updateScene = (sceneId, patch) => {
    setScenes((prev) => prev.map((s) => (s.id === sceneId ? { ...s, ...patch } : s)));
  };

  // Renomme une scène ET répercute le changement dans le texte du scénario :
  // la ligne d'en-tête de la scène est réécrite avec le nouveau titre, en
  // préservant un éventuel préfixe de numéro (ex: "1.") et un suffixe Fountain
  // (ex: "#1A#"). Les bornes des scènes/plans suivants sont décalées.
  const renameScene = (sceneId, newTitle) => {
    const scene = scenes.find((s) => s.id === sceneId);
    if (!scene) return;
    const title = String(newTitle ?? "").trim();

    const blockStart = scene.start ?? 0;
    const blockEnd = scene.end ?? blockStart;

    // Localise la ligne d'en-tête (1re ligne non vide du bloc de la scène).
    let hs = blockStart;
    while (hs < blockEnd && /\s/.test(scriptText[hs])) hs++;
    let he = scriptText.indexOf("\n", hs);
    if (he === -1 || he > blockEnd) he = blockEnd;

    // Décompose la ligne pour ne remplacer que la partie descriptive
    // (on conserve le numéro en tête et le numéro Fountain en fin).
    let core = scriptText.slice(hs, he);
    let prefix = "";
    let suffix = "";
    let m;
    if ((m = core.match(/^#\s+/))) { prefix += m[0]; core = core.slice(m[0].length); }
    if ((m = core.match(/^\.\s*/))) { prefix += m[0]; core = core.slice(m[0].length); }
    if ((m = core.match(/^\d+\s*[A-Za-z]?\s*[-.:]?\s+/))) { prefix += m[0]; core = core.slice(m[0].length); }
    if ((m = core.match(/\s*#\s*\d+\s*[A-Za-z]?\s*#\s*$/))) { suffix = m[0]; core = core.slice(0, core.length - m[0].length); }

    const newLine = prefix + title + suffix;
    const oldLine = scriptText.slice(hs, he);
    if (newLine === oldLine) {
      // Rien à changer dans le texte : on met juste à jour le titre stocké.
      setScenes((prev) => prev.map((s) => (s.id === sceneId ? { ...s, title } : s)));
      return;
    }

    const delta = newLine.length - oldLine.length;
    const nextText = scriptText.slice(0, hs) + newLine + scriptText.slice(he);

    setScriptText(nextText);
    setScenes((prev) =>
      prev.map((s) => {
        const r = shiftRangeForEdit({ start: s.start, end: s.end }, hs, he, delta);
        const patched = { ...s, start: r.start, end: r.end };
        if (s.id === sceneId) patched.title = title;
        return patched;
      })
    );
    setShots((prev) =>
      prev.map((sh) => {
        if (!sh.scriptRange) return sh;
        const r = shiftRangeForEdit(sh.scriptRange, hs, he, delta);
        return { ...sh, scriptRange: r };
      })
    );
  };

  // Décale d'un cran (haut/bas) le sous-ensemble d'éléments sélectionnés dans une
  // liste, en préservant leur regroupement (déplacement type "liste ordonnée").
  const shiftSelectedInList = (list, selectedSet, dir) => {
    const arr = list.slice();
    if (dir === "up") {
      for (let i = 1; i < arr.length; i++) {
        if (selectedSet.has(arr[i].id) && !selectedSet.has(arr[i - 1].id)) {
          [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
        }
      }
    } else {
      for (let i = arr.length - 2; i >= 0; i--) {
        if (selectedSet.has(arr[i].id) && !selectedSet.has(arr[i + 1].id)) {
          [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]];
        }
      }
    }
    return arr;
  };

  // Réordonne une ou plusieurs scènes (mode organisation). Selon sceneMoveMode :
  //  - "visual" : réordonne seulement l'affichage (uiOrder), texte inchangé.
  //  - "text"   : déplace physiquement les blocs de texte dans le scénario.
  const reorderScenes = (sceneIds, dir) => {
    const set = new Set(Array.isArray(sceneIds) ? sceneIds : [sceneIds]);
    if (set.size === 0) return;

    if (sceneMoveMode === "visual") {
      const arr = shiftSelectedInList(orderedScenes, set, dir);
      const orderById = new Map(arr.map((s, i) => [s.id, i]));
      setScenes((prev) =>
        prev.map((s) => ({ ...s, uiOrder: orderById.get(s.id) ?? s.uiOrder }))
      );
      return;
    }

    // Mode texte : on déplace physiquement les blocs de texte, en remappant
    // nous-mêmes les scènes et les plans (sans passer par la resync par ancre,
    // qui supprimerait les plans « insert » sans texte assigné).
    const byStart = scenes.slice().sort((x, y) => (x.start ?? 0) - (y.start ?? 0));
    if (byStart.length < 2) return;

    const arr = shiftSelectedInList(byStart, set, dir);
    // Aucun changement d'ordre => rien à faire.
    if (arr.every((s, i) => s.id === byStart[i].id)) return;

    const firstStart = byStart[0].start ?? 0;
    const prefix = scriptText.slice(0, firstStart);

    // Reconstruit le texte + calcule les nouvelles bornes [start,end) par scène.
    let nextText = prefix;
    let offset = prefix.length;
    const newBounds = new Map(); // id -> { start, end }
    for (const s of arr) {
      const block = scriptText.slice(s.start, s.end);
      const start = offset;
      nextText += block;
      offset += block.length;
      newBounds.set(s.id, { start, end: offset });
    }

    // Décalage de chaque plan = décalage de départ du bloc de sa scène
    // (le contenu du bloc est identique, seule sa position change).
    const deltaBySceneId = new Map();
    for (const s of byStart) {
      const b = newBounds.get(s.id);
      if (b) deltaBySceneId.set(s.id, b.start - (s.start ?? 0));
    }

    setScriptText(nextText);
    setScenes((prev) =>
      prev.map((s) => {
        const b = newBounds.get(s.id);
        return b ? { ...s, start: b.start, end: b.end, uiOrder: undefined } : s;
      })
    );
    setShots((prev) =>
      prev.map((sh) => {
        const d = deltaBySceneId.get(sh.sceneId);
        if (d == null || !sh.scriptRange) return sh;
        return {
          ...sh,
          scriptRange: {
            start: (sh.scriptRange.start ?? 0) + d,
            end: (sh.scriptRange.end ?? 0) + d,
          },
        };
      })
    );
  };

  // Marque/démarque une scène comme "supprimée" (barrée). Réversible :
  // la scène et ses plans sont masqués de la shotlist mais rien n'est effacé.
  const setSceneDeleted = (sceneId, deleted) => {
    setScenes((prev) => prev.map((s) => (s.id === sceneId ? { ...s, deleted } : s)));
    if (deleted) setSelectedSceneId((cur) => (cur === sceneId ? null : cur));
  };

  const updateShot = (shotId, patch) => {
    setShots((prev) => {
      const target = prev.find((s) => s.id === shotId);
      if (!target) return prev;

      const hasColorKey = Object.prototype.hasOwnProperty.call(patch || {}, "highlightColor");

      // If linked shot changes highlightColor => propagate color to whole group
      if (hasColorKey && target.linkGroupId) {
        const gid = target.linkGroupId;
        const sceneId = target.sceneId;
        const nextColor = patch.highlightColor;

        return prev.map((sh) => {
          if (sh.sceneId !== sceneId) return sh;

          // not in group: update only the targeted shot fully
          if ((sh.linkGroupId || null) !== gid) {
            return sh.id === shotId ? { ...sh, ...patch } : sh;
          }

          // in group: targeted gets full patch, mates only color
          if (sh.id === shotId) return { ...sh, ...patch };
          return { ...sh, highlightColor: nextColor };
        });
      }

      return prev.map((sh) => (sh.id === shotId ? { ...sh, ...patch } : sh));
    });
  };

  const updateShotTextFromSelection = (shotId, newRange) => {
    setShots((prev) => {
      const shot = prev.find((x) => x.id === shotId);
      if (!shot) return prev;

      const snippet = scriptText.slice(newRange.start, newRange.end);
      const before = scriptText.slice(Math.max(0, newRange.start - 40), newRange.start);
      const after = scriptText.slice(newRange.end, Math.min(scriptText.length, newRange.end + 40));

      const next = prev.map((x) =>
        x.id === shotId ? { ...x, scriptRange: newRange, anchor: { snippet, before, after } } : x
      );

      return renumberSceneShots(next, shot.sceneId);
    });
  };

  // Change manuellement le numéro d'un plan : le plan est déplacé à la position
  // demandée dans sa scène et tous les autres plans se renumérotent en conséquence.
  const setShotNumberManual = (shotId, targetBase) => {
    setShots((prev) => {
      const shot = prev.find((s) => s.id === shotId);
      if (!shot) return prev;

      const sceneId = shot.sceneId;
      const inScene = prev
        .filter((s) => s.sceneId === sceneId)
        .slice()
        .sort((a, b) => shotOrderKey(a) - shotOrderKey(b));

      // Regroupe (plans liés = un seul numéro de base)
      const groups = [];
      const seen = new Map();
      for (const s of inScene) {
        const key = s.linkGroupId || s.id;
        let g = seen.get(key);
        if (!g) {
          g = { key, members: [] };
          seen.set(key, g);
          groups.push(g);
        }
        g.members.push(s);
      }

      const total = groups.length;
      const curIdx = groups.findIndex((g) => g.members.some((m) => m.id === shotId));
      if (curIdx < 0) return prev;

      let target = Math.round(Number(targetBase));
      if (!Number.isFinite(target)) return prev;
      target = Math.max(1, Math.min(total, target)) - 1; // -> index 0-based
      if (target === curIdx) return prev;

      const reordered = groups.slice();
      const [moved] = reordered.splice(curIdx, 1);
      reordered.splice(target, 0, moved);

      // ordre explicite écrit sur tous les plans de la scène
      const orderById = new Map();
      reordered.forEach((g, gi) => g.members.forEach((m) => orderById.set(m.id, gi)));

      const next = prev.map((s) =>
        s.sceneId === sceneId
          ? {
              ...s,
              order: orderById.get(s.id) ?? s.order,
              listOrder: orderById.get(s.id) ?? s.listOrder,
            }
          : s
      );

      return renumberSceneShots(next, sceneId);
    });
  };

  // Applique un nouvel ordre visuel (ids) + options, puis renumérote.
  // opts.resyncBase : aligne aussi la numérotation principale (master / -A).
  // opts.becomeAnchorId / opts.oldAnchorId : transfère le "master" (cas passage au-dessus du -A).
  const applyReorderIds = (prev, sceneId, ids, opts = {}) => {
    const listMap = new Map(ids.map((id, i) => [id, i]));
    const next = prev.map((s) => {
      if (s.sceneId !== sceneId) return s;
      const ns = { ...s, listOrder: listMap.get(s.id) ?? s.listOrder };
      if (opts.resyncBase) ns.order = listMap.get(s.id) ?? ns.order;
      if (opts.becomeAnchorId && s.id === opts.becomeAnchorId) ns.isMaster = true;
      if (opts.oldAnchorId && s.id === opts.oldAnchorId) ns.isMaster = false;
      return ns;
    });
    return renumberSceneShots(next, sceneId);
  };

  // Applique un nouvel ordre de plans (ids) : renumérote, et si le réglage
  // shotReorderMovesText est actif, réordonne aussi les blocs de texte assignés
  // dans le scénario pour qu'ils suivent le nouvel ordre.
  const applyShotReorder = (sceneId, ids, opts = {}) => {
    const reordered = applyReorderIds(shots, sceneId, ids, opts);
    const res = shotReorderMovesText
      ? reorderSceneTextForOrder(scriptText, shots, sceneId, ids)
      : null;

    if (!res) {
      setShots(reordered);
      return;
    }

    const { text, newRangesById } = res;
    const finalShots = reordered.map((s) => {
      const r = newRangesById.get(s.id);
      if (!r) return s;
      const snippet = text.slice(r.start, r.end);
      const before = text.slice(Math.max(0, r.start - 40), r.start);
      const after = text.slice(r.end, Math.min(text.length, r.end + 40));
      return { ...s, scriptRange: r, anchor: { snippet, before, after } };
    });
    setScriptText(text);
    setShots(finalShots);
  };

  // Drag & drop : déplace UNIQUEMENT ce plan à la position de la cible.
  const reorderShotByDrag = (sceneId, dragId, targetId, opts = {}) => {
    if (dragId === targetId) return;
    const ids = shots
      .filter((s) => s.sceneId === sceneId)
      .slice()
      .sort((a, b) => shotListKey(a) - shotListKey(b))
      .map((s) => s.id);

    const from = ids.indexOf(dragId);
    const origTarget = ids.indexOf(targetId);
    if (from < 0 || origTarget < 0) return;

    ids.splice(from, 1);
    let to = ids.indexOf(targetId);
    if (to < 0) to = ids.length;
    if (from < origTarget) to += 1; // glissé vers le bas => insère après la cible
    to = Math.max(0, Math.min(ids.length, to));
    ids.splice(to, 0, dragId);

    applyShotReorder(sceneId, ids, opts);
  };

  // Boutons ▲▼ : déplace le plan sélectionné d'un cran (échange avec le voisin).
  const moveShotStep = (sceneId, shotId, dir, opts = {}) => {
    const ids = shots
      .filter((s) => s.sceneId === sceneId)
      .slice()
      .sort((a, b) => shotListKey(a) - shotListKey(b))
      .map((s) => s.id);

    const i = ids.indexOf(shotId);
    const j = dir === "up" ? i - 1 : i + 1;
    if (i < 0 || j < 0 || j >= ids.length) return;

    [ids[i], ids[j]] = [ids[j], ids[i]];
    applyShotReorder(sceneId, ids, opts);
  };

  // Supprime uniquement le texte assigné d'un plan (le transforme en insert).
  const clearShotText = (shotId) => {
    setShots((prev) => {
      const shot = prev.find((s) => s.id === shotId);
      if (!shot) return prev;

      const pos = shot.scriptRange?.start ?? 0;
      const next = prev.map((s) =>
        s.id === shotId
          ? {
              ...s,
              scriptRange: { start: pos, end: pos },
              anchor: { snippet: "", before: "", after: "" },
            }
          : s
      );
      return renumberSceneShots(next, shot.sceneId);
    });
  };

  const deleteShot = (shotId) => {
    setShots((prev) => {
      const shot = prev.find((s) => s.id === shotId);
      if (!shot) return prev;

      const filtered = prev.filter((s) => s.id !== shotId);
      return renumberSceneShots(filtered, shot.sceneId);
    });

    setSelectedShotId(null);
  };

  // ---------------------------
  // Organisation : opérations en lot sur les plans
  // ---------------------------
  const deleteShots = (shotIds) => {
    const ids = new Set(Array.isArray(shotIds) ? shotIds : [shotIds]);
    if (ids.size === 0) return;

    setShots((prev) => {
      const affected = new Set(
        prev.filter((s) => ids.has(s.id)).map((s) => s.sceneId)
      );
      let next = prev.filter((s) => !ids.has(s.id));
      for (const sid of affected) next = renumberSceneShots(next, sid);
      return next;
    });

    setSelectedShotId((cur) => (ids.has(cur) ? null : cur));
  };

  // Déplace les plans sélectionnés vers une scène précise (par id).
  // Selon sceneMoveMode : en mode « texte », le texte assigné de chaque plan est
  // aussi déplacé dans le scénario (vers la fin du bloc de la scène cible).
  const moveShotsToScene = (shotIds, sceneId) => {
    const idList = (Array.isArray(shotIds) ? shotIds : [shotIds]).filter(Boolean);
    if (idList.length === 0) return;
    if (!scenes.some((s) => s.id === sceneId)) return;

    // --- Mode visuel : simple réassignation (insert au début de la scène cible)
    if (sceneMoveMode !== "text") {
      const ids = new Set(idList);
      setShots((prev) => {
        const target = scenes.find((s) => s.id === sceneId);
        const pos = target?.start ?? 0;
        const affected = new Set([sceneId]);
        let next = prev.map((sh) => {
          if (!ids.has(sh.id) || sh.sceneId === sceneId) return sh;
          affected.add(sh.sceneId);
          return {
            ...sh,
            sceneId,
            scriptRange: { start: pos, end: pos },
            anchor: { snippet: "", before: "", after: "" },
            order: undefined,
            listOrder: undefined,
          };
        });
        for (const sid of affected) next = renumberSceneShots(next, sid);
        return next;
      });
      return;
    }

    // --- Mode texte : on déplace aussi le texte assigné de chaque plan.
    let workText = scriptText;
    let workScenes = scenes.map((s) => ({ ...s }));
    let workShots = shots.map((s) => ({ ...s }));
    const affected = new Set([sceneId]);

    for (const shotId of idList) {
      const shot = workShots.find((s) => s.id === shotId);
      if (!shot || shot.sceneId === sceneId) continue;
      affected.add(shot.sceneId);

      const target = workScenes.find((s) => s.id === sceneId);
      if (!target) continue;

      const s0 = shot.scriptRange?.start ?? 0;
      const e0 = shot.scriptRange?.end ?? s0;

      // Plan « insert » sans texte : réassignation à la fin de la scène cible.
      if (e0 <= s0) {
        const insPos = target.end ?? target.start ?? 0;
        const idx = workShots.findIndex((s) => s.id === shotId);
        workShots[idx] = {
          ...shot,
          sceneId,
          scriptRange: { start: insPos, end: insPos },
          anchor: { snippet: "", before: "", after: "" },
          order: undefined,
          listOrder: undefined,
        };
        continue;
      }

      const block = workText.slice(s0, e0);
      const len = e0 - s0;

      // 1) Suppression de [s0, e0)
      const text2 = workText.slice(0, s0) + workText.slice(e0);

      // 2) Point d'insertion = fin du bloc de la scène cible (en coords text2)
      const te = target.end ?? workText.length;
      const insertPos = te <= s0 ? te : te >= e0 ? te - len : s0;

      // Séparateurs de ligne pour ne pas coller le texte déplacé.
      const prevChar = text2[insertPos - 1];
      const nextChar = text2[insertPos];
      const pre = insertPos > 0 && prevChar !== "\n" ? "\n" : "";
      const post =
        block.endsWith("\n") || insertPos >= text2.length || nextChar === "\n"
          ? ""
          : "\n";
      const ins = pre + block + post;
      const insLen = ins.length;
      const blockOffsetInIns = pre.length;

      const text3 = text2.slice(0, insertPos) + ins + text2.slice(insertPos);

      // Remap de toute position d'origine -> nouvelle (suppression puis insertion)
      const remap = (p) => {
        let q = p <= s0 ? p : p >= e0 ? p - len : s0;
        if (q >= insertPos) q += insLen;
        return q;
      };

      workScenes = workScenes.map((sc) => ({
        ...sc,
        start: remap(sc.start ?? 0),
        end: remap(sc.end ?? 0),
      }));

      const newStart = insertPos + blockOffsetInIns;
      const newEnd = newStart + len;

      workShots = workShots.map((sh) => {
        if (sh.id === shotId) {
          const before = text3.slice(Math.max(0, newStart - 40), newStart);
          const after = text3.slice(newEnd, Math.min(text3.length, newEnd + 40));
          return {
            ...sh,
            sceneId,
            scriptRange: { start: newStart, end: newEnd },
            anchor: { snippet: block, before, after },
            order: undefined,
            listOrder: undefined,
          };
        }
        if (!sh.scriptRange) return sh;
        return {
          ...sh,
          scriptRange: { start: remap(sh.scriptRange.start), end: remap(sh.scriptRange.end) },
        };
      });

      workText = text3;
    }

    for (const sid of affected) workShots = renumberSceneShots(workShots, sid);

    setScriptText(workText);
    setScenes(workScenes);
    setShots(workShots);
  };

  // Réordonne les plans sélectionnés d'un cran (haut/bas) dans leur scène.
  const reorderShotsInScene = (sceneId, shotIds, dir) => {
    const set = new Set(Array.isArray(shotIds) ? shotIds : [shotIds]);
    if (set.size === 0) return;
    const inScene = shots
      .filter((s) => s.sceneId === sceneId)
      .slice()
      .sort((a, b) => shotListKey(a) - shotListKey(b))
      .map((s) => ({ id: s.id }));
    const arr = shiftSelectedInList(inScene, set, dir).map((o) => o.id);
    applyShotReorder(sceneId, arr, { resyncBase: true });
  };

  // Applique un ordre complet de plans (fenêtre de renumérotation).
  const applyShotOrder = (sceneId, orderedIds) => {
    if (!Array.isArray(orderedIds) || orderedIds.length === 0) return;
    applyShotReorder(sceneId, orderedIds, { resyncBase: true });
  };

  // ---------------------------
  // Linking shots
  // ---------------------------
  const makeLinkGroupId = () =>
    `lg_${
      globalThis.crypto?.randomUUID?.() ??
      `t${Date.now()}_${Math.random().toString(16).slice(2)}`
    }`;

  const linkShotsInScene = (sceneId, aId, bId) => {
    setShots((prev) => {
      const inScene = prev.filter((s) => s.sceneId === sceneId);
      const a = inScene.find((s) => s.id === aId);
      const b = inScene.find((s) => s.id === bId);
      if (!a || !b || a.id === b.id) return prev;

      const groupA = a.linkGroupId || null;
      const groupB = b.linkGroupId || null;

      if (groupA && groupA === groupB) return prev;

      const membersA = groupA ? inScene.filter((s) => s.linkGroupId === groupA) : [a];
      const membersB = groupB ? inScene.filter((s) => s.linkGroupId === groupB) : [b];

      const membersAIds = new Set(membersA.map((s) => s.id));
      const membersBIds = new Set(membersB.map((s) => s.id));

      const unionMap = new Map();
      for (const s of [...membersA, ...membersB]) unionMap.set(s.id, s);

      const union = Array.from(unionMap.values()).sort(
        (x, y) => (x.scriptRange?.start ?? 0) - (y.scriptRange?.start ?? 0)
      );

      // ref shot = earliest in text
      const refShot = union[0] || null;
      const refColor = String(refShot?.highlightColor ?? "").trim();

      // apply color + tags to the "joining" group (not the ref group)
      const applyToIds = refShot && membersAIds.has(refShot.id) ? membersBIds : membersAIds;
      const refTags = refShot ? pickLinkedTags(refShot) : null;

      const newGroupId = groupA || groupB || makeLinkGroupId();
      const unionIds = new Set(union.map((s) => s.id));

      const next = prev.map((sh) => {
        if (sh.sceneId !== sceneId) return sh;
        if (!unionIds.has(sh.id)) return sh;

        const inheritPatch = refTags && applyToIds.has(sh.id) ? refTags : null;

        return {
          ...sh,
          ...(inheritPatch ? inheritPatch : {}),
          linkGroupId: newGroupId,
          highlightColor: refColor && applyToIds.has(sh.id) ? refColor : sh.highlightColor ?? "",
        };
      });

      return renumberSceneShots(next, sceneId);
    });
  };

  const unlinkShotFromGroup = (sceneId, shotId) => {
    setShots((prev) => {
      const shot = prev.find((s) => s.id === shotId);
      if (!shot || shot.sceneId !== sceneId) return prev;

      const next = prev.map((sh) => (sh.id === shotId ? { ...sh, linkGroupId: null } : sh));
      return renumberSceneShots(next, sceneId);
    });
  };

  // ---------------------------
  // Project load
  // ---------------------------
  const handleProjectLoaded = (project) => {
    const text = typeof project?.scriptText === "string" ? project.scriptText : "";

    // scenes: prefer project scenes to preserve IDs for shots
    const loadedScenes = (Array.isArray(project?.scenes) ? project.scenes : parseScenes(text)).map(
      (sc) => ({
        ...sc,
        // ✅ compat anciens projets : dérive le numéro depuis le titre si absent
        number:
          sc?.number != null && String(sc.number).trim()
            ? String(sc.number).trim()
            : extractSceneNumber(sc?.title || ""),
      })
    );

    let loadedShots = Array.isArray(project?.shots) ? project.shots : [];

    const safeNumber = (v, fallback = 0) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : fallback;
    };

    loadedShots = loadedShots.map((sh) => ({
      id: sh?.id ?? makeId(),
      sceneId: sh?.sceneId ?? null,
      scriptRange: {
        start: safeNumber(sh?.scriptRange?.start, 0),
        end: safeNumber(sh?.scriptRange?.end, 0),
      },
      shotNumber: sh?.shotNumber ?? "",
      description: sh?.description ?? "",
      lensMm: sh?.lensMm ?? "",
      shotSize: sh?.shotSize ?? "",
      angle: sh?.angle ?? sh?.shotType ?? "",
      shotType: sh?.shotType ?? "", // legacy
      focus: sh?.focus ?? "",
      framing: sh?.framing ?? "",
      movement: sh?.movement ?? "",
      lights: sh?.lights ?? "",
      durationEstimateSec: safeNumber(sh?.durationEstimateSec, 0),
      linkGroupId: sh?.linkGroupId ?? null,
      order: Number.isFinite(sh?.order) ? sh.order : undefined,
      listOrder: Number.isFinite(sh?.listOrder) ? sh.listOrder : undefined,
      image: sh?.image ?? null,
      isMaster: !!sh?.isMaster,
      frameFormat: sh?.frameFormat || "",
      colorFormat: sh?.colorFormat || "",
      highlightColor: sh?.highlightColor ?? "",
      anchor: sh?.anchor ?? null, // if present
    }));

    // drop shots pointing to non-existing scenes
    const sceneIds = new Set((loadedScenes || []).map((s) => s.id));
    loadedShots = loadedShots.filter((sh) => sceneIds.has(sh.sceneId));

    // renumber per scene
    for (const sid of sceneIds) loadedShots = renumberSceneShots(loadedShots, sid);

    setScriptText(text);
    setScenes(loadedScenes);

    const firstSceneId = loadedScenes?.[0]?.id ?? null;
    const nextSelectedSceneId = loadedScenes?.some((s) => s.id === project?.selectedSceneId)
      ? project.selectedSceneId
      : firstSceneId;

    setSelectedSceneId(nextSelectedSceneId);
    setShots(loadedShots);

    const nextSelectedShotId = loadedShots.some((s) => s.id === project?.selectedShotId)
      ? project.selectedShotId
      : null;

    setSelectedShotId(nextSelectedShotId);

    setScriptViewMode(project?.scriptViewMode === "scene" ? "scene" : "full");
    setSceneSelection({ start: 0, end: 0 });
    setScriptSelection({ start: 0, end: 0 });

    liveEditsSinceSyncRef.current = false;
    dirtyCleanRef.current = true;
    setProjectDirty(false);
    if (typeof project?.fileName === "string") setFileName(project.fileName);
  };

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <div id="appBurgerSlot" className="appBurgerSlot" />
          <img src={logo} alt="Logo" />
        </div>

        <div className="hint topbarRight">
          <button
            type="button"
            className={`topbarIconBtn saveButton ${projectDirty ? "isDirty" : ""}`}
            onClick={handleSaveProject}
            disabled={!canSaveProject}
            aria-label={t("menu_saveProject")}
            title={`${t("menu_saveProject")} (Ctrl+S)`}
          >
            💾
          </button>

          <button
            type="button"
            className="topbarIconBtn aboutButton"
            onClick={() => setAboutOpen(true)}
            aria-label={t("menu.about")}
            title={t("menu.about")}
          >
            ℹ️
          </button>

          <button
            type="button"
            className="versionButton"
            onClick={() => setIsChangelogOpen(true)}
            aria-label={t ? t("changelog.openAria") : "Open changelog"}
            title={t ? t("changelog.openTitle") : "Changelog"}
          >
            {APP_VERSION}
          </button>
        </div>
      </header>

      {syncToast && (
        <div
          style={{
            position: "fixed",
            top: 70,
            right: 16,
            zIndex: 9999,
            background: "#0c0e13",
            border: "1px solid #22283a",
            padding: "10px 12px",
            borderRadius: 12,
            boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
            maxWidth: 360,
            color: "white",
            fontSize: 13,
          }}
        >
          {syncToast}
        </div>
      )}

      <main className={`main ${scriptCollapsed ? "scriptCollapsed" : ""}`}>
        <section className={`panel ${scriptCollapsed ? "panelCollapsed" : ""}`}>
          <ScriptPanel
            scriptCollapsed={scriptCollapsed}
            onToggleScriptCollapsed={() => setScriptCollapsed((v) => !v)}
            editorInScriptPanel={editorInScriptPanel}
            onToggleEditorInScriptPanel={() => setEditorInScriptPanel((v) => !v)}
            scriptText={scriptText}
            setScriptText={setScriptText}
            onScriptLoaded={handleScriptLoaded}
            onProjectLoaded={handleProjectLoaded}
            onImportShotsFromCsv={importShotsFromCsv}
            fileName={fileName}
            setFileName={setFileName}
            scenes={scenes}
            selectedSceneId={selectedSceneId}
            scriptViewMode={scriptViewMode}
            onToggleView={toggleScriptView}
            sceneSelection={sceneSelection}
            setSceneSelection={setSceneSelection}
            scriptSelection={scriptSelection}
            setScriptSelection={setScriptSelection}
            shots={shots}
            selectedShotId={selectedShotId}
            setSelectedShotId={setSelectedShotId}
            includeLinkedShotsInCounts={includeLinkedShotsInCounts}
            setIncludeLinkedShotsInCounts={setIncludeLinkedShotsInCounts}
            linkedLetterReorder={linkedLetterReorder}
            setLinkedLetterReorder={setLinkedLetterReorder}
            sceneMoveMode={sceneMoveMode}
            setSceneMoveMode={setSceneMoveMode}
            shotReorderMovesText={shotReorderMovesText}
            setShotReorderMovesText={setShotReorderMovesText}
            onApplyScriptEdits={applyScriptEdits}
            onScriptEditDelta={handleScriptEditDelta}
            shotPrefixMode={shotPrefixMode}
            setShotPrefixMode={setShotPrefixMode}
            shotEditorTermsLang={shotEditorTermsLang}
            setShotEditorTermsLang={setShotEditorTermsLang}
            shotFrameFormat={shotFrameFormat}
            setShotFrameFormat={setShotFrameFormat}
          />
        </section>

        <section className="panel">
          <SceneShotPanel
            scriptText={scriptText}
            scenes={orderedScenes}
            shots={shots}
            organizeMode={organizeMode}
            setOrganizeMode={setOrganizeMode}
            onDeleteShots={deleteShots}
            onMoveShotsToSceneId={moveShotsToScene}
            onReorderShotsInScene={reorderShotsInScene}
            onApplyShotOrder={applyShotOrder}
            onReorderScenes={reorderScenes}
            onSetSceneDeleted={setSceneDeleted}
            onRenameScene={renameScene}
            selectedSceneId={selectedSceneId}
            setSelectedSceneId={(id) => {
              setSelectedSceneId(id);
              setSceneSelection({ start: 0, end: 0 });
            }}
            onAddShotFromSelection={addShotFromSelection}
            onAddShotWithoutText={addShotWithoutText}
            editorInScriptPanel={editorInScriptPanel}
            selectedShotId={selectedShotId}
            setSelectedShotId={setSelectedShotId}
            onUpdateShot={updateShot}
            onUpdateShotTextFromSelection={updateShotTextFromSelection}
            scriptViewMode={scriptViewMode}
            sceneSelection={sceneSelection}
            scriptSelection={scriptSelection}
            onDeleteShot={deleteShot}
            onClearShotText={clearShotText}
            onSetShotNumber={setShotNumberManual}
            onReorderShotByDrag={reorderShotByDrag}
            onMoveShotStep={moveShotStep}
            onLinkShots={linkShotsInScene}
            onUnlinkShot={unlinkShotFromGroup}
            onUpdateScene={updateScene}
            includeLinkedShotsInCounts={includeLinkedShotsInCounts}
            linkedLetterReorder={linkedLetterReorder}
            shotPrefixMode={shotPrefixMode}
            shotEditorTermsLang={shotEditorTermsLang}
            shotFieldLayout={shotFieldLayout}
            setShotFieldLayout={setShotFieldLayout}
            shotFrameFormat={shotFrameFormat}
          />
        </section>
      </main>

      {isChangelogOpen && (
        <ChangelogModal onClose={() => setIsChangelogOpen(false)} changelog={CHANGELOG} t={t} />
      )}

      {aboutOpen && (
        <div
          className="modalOverlay"
          role="dialog"
          aria-modal="true"
          aria-label={t("about.title")}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setAboutOpen(false);
          }}
        >
          <div className="modalCard modalW_about" onMouseDown={(e) => e.stopPropagation()}>
            <button
              className="modalCloseButton"
              type="button"
              aria-label="Close"
              onClick={() => setAboutOpen(false)}
              title="Close"
            >
              ✕
            </button>

            <h3 className="modalTitle">{t("about.title")}</h3>
            <div className="modalBody">
              <hr className="aboutSep" />
              <p>{t("about.created", { name: "Val-de-Travers" })}</p>
              <p>
                {t("about.tip")}{" "}
                <a href="https://ko-fi.com/val_de_travers" target="_blank" rel="noreferrer">
                  https://ko-fi.com/val_de_travers
                </a>
              </p>
              <p>{t("about.mail-phrase")}</p>
              <p>{t("about.mail", { name: "val@shotlist-builder.com" })}</p>
              <hr className="aboutSep" />
              <p>{t("about.notStoredOnline")}</p>
              <hr className="aboutSep" />
              <p>{t("about.thanks", { name: "Coco, Alexandre Etzlinger, Association Lokwel Films" })}</p>
              <hr className="aboutSep" />
              <p>{t("about.free")}</p>
              <hr className="aboutSep" />
              <p>{t("about.stack")}</p>
              <p>{t("about.ai")}</p>
              <hr className="aboutSep" />
              <p>{t("about.version")}v{APP_VERSION}</p>
            </div>
          </div>
        </div>
      )}

      {savePopupOpen && (
        <div
          className="modalOverlay"
          role="dialog"
          aria-modal="true"
          aria-label={t("saveProjectPopup_title")}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setSavePopupOpen(false);
          }}
        >
          <div className="modalCard scriptRefreshModal modalW_refresh">
            <button
              className="modalCloseButton"
              type="button"
              aria-label="Fermer"
              onClick={() => setSavePopupOpen(false)}
              title="Fermer"
            >
              ×
            </button>

            <h3 className="modalTitle">{t("saveProjectPopup_title")}</h3>

            <div className="modalBody">
              <p>{t("saveProjectPopup_p1")}</p>
              <p>{t("saveProjectPopup_p2")}</p>
              <p>{t("saveProjectPopup_p3")}</p>

              <div className="modalFooter">
                <button className="btn" type="button" onClick={() => setSavePopupOpen(false)}>
                  {t("saveProjectPopup_ok")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
